import { BLACK_CTA_VENDA, BLACK_ULTIMOS_DIAS } from "@/lib/black2x1/escassez";
/**
 * Renderização de UM slide do banner rotativo da home.
 *
 * Compartilhado entre a home (carrossel) e o preview do admin
 * (/embed/banner-slide/$key), para que o preview seja fiel ao real.
 */
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useBlackSale } from "@/hooks/use-black-sale";
import { blackScheduledHref, BLACK_PUBLIC_OPENS_AT } from "@/lib/black2x1/offer-rules";

import type { Slide } from "@/components/home/banner-slides";

type Props = {
  slide: Slide;
  /** Índice no carrossel (afeta prioridade de carregamento). */
  index?: number;
  total?: number;
  /** Classes de posicionamento/transição aplicadas no wrapper. */
  className?: string;
  /** Oculta o parágrafo de descrição (usado no destaque do catálogo). */
  hideDescription?: boolean;
  ariaHidden?: boolean;
  /** Já pode carregar as imagens de fundo. */
  showBgImage?: boolean;
  /** No preview o CTA não navega. */
  preview?: boolean;
  /** Versão compacta (30% menor em altura), usada no destaque do catálogo. */
  compact?: boolean;
  /** Altura pelo conteúdo, com respiro igual em cima e embaixo. */
  autoHeight?: boolean;
};

export function BannerSlideView({
  slide,
  index = 0,
  total = 1,
  className = "",
  ariaHidden = false,
  showBgImage = true,
  preview = false,
  hideDescription = false,
  compact = false,
  autoHeight = false,
}: Props) {
  const sale = useBlackSale();
  const ctaHref = sale ? blackScheduledHref(slide.ctaHref, BLACK_PUBLIC_OPENS_AT) : slide.ctaHref;
  const i = index;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CtaTag: any = preview ? "span" : slide.ctaInternal ? Link : "a";
  const ctaProps = preview
    ? {}
    : slide.ctaInternal
      ? { to: ctaHref }
      : ctaHref.startsWith("/")
        ? { href: ctaHref }
        : { href: ctaHref, target: "_blank", rel: "noopener noreferrer" };

  return (
    <div
      className={`${className} ${slide.bgClass} ${autoHeight ? "" : compact ? "h-[350px] sm:h-[392px] md:h-[420px] lg:h-[448px]" : "h-[500px] sm:h-[560px] md:h-[600px] lg:h-[640px]"} flex flex-col overflow-hidden`}
      aria-hidden={ariaHidden}
      role="group"
      aria-roledescription="slide"
      aria-label={`${i + 1} de ${total}`}
    >
      {slide.bgStyle && (
        <div className="absolute inset-0 pointer-events-none" style={{ background: slide.bgStyle }} aria-hidden />
      )}
      {slide.topBlur && (
        <div
          className="pointer-events-none absolute -top-56 left-1/2 h-[520px] w-[1200px] max-w-none -translate-x-1/2 rounded-full opacity-[0.28] blur-[90px]"
          style={{ background: slide.topBlur }}
          aria-hidden
        />
      )}
      {slide.stripes && (
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(115deg, rgba(255,255,255,0.9) 0 2px, transparent 2px 26px)",
            maskImage: "linear-gradient(180deg, rgba(0,0,0,1), transparent 85%)",
          }}
          aria-hidden
        />
      )}
      {slide.bgImage && slide.bgImageClass && showBgImage && (
        <img
          src={slide.bgImage}
          srcSet={slide.bgImageSrcSet}
          sizes={slide.bgImageSizes}
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          className={`absolute inset-0 w-full h-full pointer-events-none ${slide.bgImageClass}`}
          loading="eager"
          fetchPriority={i === 0 ? "high" : "low"}
          decoding={i === 0 ? "sync" : "async"}
        />
      )}
      {slide.bgImage && !slide.bgImageClass && showBgImage && (
        <div
          className="absolute inset-0 pointer-events-none bg-center bg-cover"
          style={{ backgroundImage: `url(${slide.bgImage})` }}
          aria-hidden
        />
      )}
      {slide.bgImage && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              slide.bgOverlay ??
              "linear-gradient(180deg, rgba(5,18,42,0.55) 0%, rgba(5,18,42,0.75) 55%, rgba(5,18,42,0.9) 100%)",
          }}
          aria-hidden
        />
      )}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, ${slide.accent} 0, transparent 40%), radial-gradient(circle at 80% 70%, #ffffff 0, transparent 45%)`,
        }}
      />
      <div className={`relative container mx-auto px-5 sm:px-6 ${autoHeight ? "py-14 sm:py-16 md:py-20" : "py-10 sm:py-14 md:py-16"} flex-1 flex flex-col justify-center`}>
        <div
          className={`grid ${slide.noImage ? "lg:grid-cols-1" : "lg:grid-cols-[1fr_1.05fr]"} gap-4 sm:gap-8 lg:gap-10 items-center lg:items-stretch ${slide.align === "left" ? "max-w-6xl mr-auto ml-0" : "max-w-6xl mx-auto"}`}
        >
          {!slide.noImage && (
            <div className="hidden lg:block relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative h-full flex items-center">
                  <div
                    className="absolute -inset-3 rounded-2xl blur-xl"
                    style={{ background: `linear-gradient(135deg, ${slide.accent}40, transparent)` }}
                  />
                  <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/30 h-full">
                    <img
                      src={slide.image}
                      alt={slide.imageAlt}
                      width={640}
                      height={640}
                      className="block h-full w-auto object-contain"
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : "auto"}
                      decoding={i === 0 ? "sync" : "async"}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className={
              slide.align === "left"
                ? "text-left max-w-2xl"
                : slide.noImage
                  ? "text-center mx-auto"
                  : "text-center lg:text-left mx-auto lg:mx-0"
            }
          >
            {slide.logo && (
              <img
                src={slide.logo}
                alt={slide.logoAlt ?? ""}
                className={`${slide.logoClass ?? "h-8 sm:h-11 md:h-12"} w-auto max-w-full ${slide.logoClass ? "" : "mb-2 sm:mb-5"} ${slide.align === "left" ? "" : slide.noImage ? "mx-auto" : "mx-auto lg:mx-0"}`}
                loading="lazy"
              />
            )}
            {slide.eyebrow && (
              <p
                className="font-semibold uppercase tracking-widest text-[11px] sm:text-sm mb-2 sm:mb-3"
                style={{ color: slide.accent }}
              >
                {slide.eyebrow}
              </p>
            )}
            {slide.key === "institucional" ? (
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] font-extrabold leading-[1.05] tracking-tight">
                {slide.title}
              </h2>
            ) : (
              <h2
                className={`font-extrabold leading-[1.1] tracking-tight ${slide.key === "cbfeo" ? "text-[clamp(0.95rem,4.6vw,2.6rem)] whitespace-nowrap" : compact ? "text-lg sm:text-2xl md:text-3xl lg:text-[2rem]" : "text-xl sm:text-3xl md:text-4xl lg:text-[2.6rem]"}`}
              >
                {slide.title}
              </h2>
            )}
            {slide.descLines && slide.descLines.length > 0 && (
              <ul className="mt-5 space-y-1.5 text-base font-bold text-white sm:mt-7 sm:text-xl">
                {slide.descLines.map((l) => <li key={l}>{l}</li>)}
              </ul>
            )}
            {!hideDescription && slide.desc && (
              <p
                className={`mt-3 sm:mt-4 ${slide.descClass ?? "text-white/85"} text-sm sm:text-base md:text-lg leading-relaxed px-1 sm:px-0 ${slide.align === "left" ? "max-w-xl" : slide.noImage ? "max-w-2xl mx-auto" : "max-w-2xl mx-auto lg:mx-0 lg:max-w-xl"}`}
              >
                {slide.desc}
              </p>
            )}

            {slide.tagline && (
              <p className={`mt-3 sm:mt-4 font-extrabold uppercase tracking-wider ${slide.taglineClass ?? "text-[#FFBC57] text-lg sm:text-xl md:text-2xl"}`}>
                {slide.tagline}
              </p>
            )}



            {slide.divider && (
              <div
                className="mx-auto mt-5 h-px w-full max-w-2xl sm:mt-6"
                style={{ background: slide.divider, opacity: 0.7 }}
                aria-hidden
              />
            )}
            {slide.pills && slide.pills.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 sm:mt-5 sm:gap-3 sm:text-xs">
                {slide.pills.map((pill) => (
                  <span key={pill} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 sm:px-3 sm:py-1.5">
                    {pill}
                  </span>
                ))}
              </div>
            )}

            {sale && slide.key === "black2x1" ? (
              <div className="mt-5 flex flex-col items-center gap-2 sm:mt-6">
                <p className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2fe0d6] sm:text-sm">🔥 {BLACK_ULTIMOS_DIAS} · vendas abertas</p>
              </div>
            ) : null}
            <div className="mt-5 sm:mt-7">
              <CtaTag
                {...ctaProps}
                className={`inline-flex items-center gap-2 rounded-full font-bold px-5 sm:px-7 py-2.5 sm:py-3.5 text-sm sm:text-base transition-all shadow-lg shadow-black/20 ${slide.ctaClass}`}
              >
                {sale && slide.key === "black2x1" ? BLACK_CTA_VENDA : slide.ctaLabel} <ArrowRight className="w-4 h-4" />
              </CtaTag>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
